import { useCallback, useMemo, useState, type ReactNode } from 'react';
import { useExtensionsStore } from '@/stores/extensions';
import { Bar, BarChart, CartesianGrid, XAxis, YAxis } from 'recharts';
import {
  AlertTriangle,
  Copy,
  Download,
  FileText,
  Loader2,
  MoreHorizontal,
  Pencil,
  Play,
  Plus,
  Save,
  Share2,
  Sparkles,
  Trash2,
  X,
} from 'lucide-react';
import {
  DEFAULT_TIME_REPORT_CONFIG,
  type TimeReportConfig,
  type TimeReportGroupByLevel,
} from '@hygge/shared';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { DatePicker } from '@/components/ui/date-picker';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from '@/components/ui/chart';
import {
  FilterProvider,
  MultiSelectFilter,
  useFilterContext,
  useFilterOptions,
  type FilterConfig,
  type FilterFieldConfig,
  type FilterOption,
} from '@/components/filters';
import {
  useDeleteSavedReport,
  useDraftReportConfig,
  useDuplicateSavedReport,
  useRunTimeReport,
  useSaveReport,
  useSavedReports,
  useUpdateSavedReport,
} from '@/hooks/use-time-entries';
import type { SavedTimeReport, TimeReportRunResult } from '@/lib/time-entries';
import { extractErrorMessage } from '@/lib/errors';
import { usePermission } from '@/lib/permissions';
import { useAuthStore } from '@/stores/auth';
import { cn } from '@/lib/utils';
import { formatDuration, formatHours } from '../lib/report-print';
import {
  GROUP_LEVELS,
  MAX_GROUP_LEVELS,
  PEOPLE_ATTRIBUTE_NOTICE,
  REPORT_FILTERS,
  REPORT_METRICS,
  WEEK_WINDOW_CUT_NOTICE,
  WEEK_WINDOW_NOTICE,
  availableFilterKeys,
  canManageReport,
  canSetTimeAxis,
  chartSeries,
  clearFilter,
  cloneConfig,
  configFingerprint,
  describeFilters,
  filterDescriptor,
  formatWindow,
  fromFilterState,
  groupByOf,
  groupingOf,
  levelLabel,
  setCompletedPeriodsOnly,
  ownerName,
  parseSavedConfig,
  rowsSumSeconds,
  setTimeAxis,
  splitSavedReports,
  toFilterState,
  toggleLevel,
  toggleMetric,
  totalsAgree,
  unresolvedFieldLabel,
  usesPeopleAttributes,
  visibleFilterKeys,
  type FilterOptionLabels,
  type ReportFilterKey,
  type ReportFilterState,
} from '../lib/report-builder-state';
import { exportBuiltReport } from '../lib/report-export';
import { ResultTable } from './report-result-table';

/**
 * The Builder: a report as a configuration you can assemble, describe, run,
 * save and share.
 *
 * Two rules shape everything here. The model never produces a number — it fills
 * this form, and every figure on screen comes back from `/report/run`. And the
 * screen never resolves a period itself: presets and the whole-week growth a
 * week axis triggers are the server's arithmetic, so what is printed above a
 * total is always `result.window`, never the period that was asked for.
 */

const PERIOD_PRESETS: Array<{ value: TimeReportConfig['period']['preset']; label: string }> = [
  { value: 'this-month', label: 'This Month' },
  { value: 'last-month', label: 'Last Month' },
  { value: 'this-week', label: 'This Week' },
  { value: 'last-week', label: 'Last Week' },
  { value: 'custom', label: 'Custom Range' },
];

const HOURS_CHART: ChartConfig = {
  hours: { label: 'Hours', color: 'var(--color-hygge)' },
};

function toDayString(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/** 'yyyy-MM-dd' → a local Date the picker can show, without a timezone slip. */
function fromDayString(day: string | undefined): Date | null {
  if (!day) return null;
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(day);
  if (!match) return null;
  return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
}

function labelsOf(options: FilterOption[]): ReadonlyMap<string, string> {
  return new Map(options.map((option) => [option.value, option.label]));
}

/**
 * Every option list the filter row can draw on, held once at the top.
 *
 * The controls fetch their own options, but they are mounted only while their
 * filter is on screen — and the EXPORT has to name a constraint whether or not
 * its control is visible, because the document leaves the building on its own.
 * These are the same cached queries the controls use, so this costs requests
 * only for lists nothing else on the screen has asked for.
 */
function useFilterOptionLabels(): FilterOptionLabels {
  const projects = useFilterOptions('projects');
  const employees = useFilterOptions('employees');
  const departments = useFilterOptions('departments');
  const positions = useFilterOptions('positions');
  const employmentTypes = useFilterOptions('employmentTypes');
  const projectManagers = useFilterOptions('projectManagers');

  return useMemo(
    () => ({
      projects: labelsOf(projects.options),
      employees: labelsOf(employees.options),
      departments: labelsOf(departments.options),
      positions: labelsOf(positions.options),
      employmentTypes: labelsOf(employmentTypes.options),
      projectManagers: labelsOf(projectManagers.options),
    }),
    [
      projects.options,
      employees.options,
      departments.options,
      positions.options,
      employmentTypes.options,
      projectManagers.options,
    ],
  );
}

/** One field description, used by both the provider's state and the controls. */
function fieldConfigOf(key: ReportFilterKey): FilterFieldConfig<string[]> {
  const descriptor = filterDescriptor(key);
  return {
    key: descriptor.key,
    type: 'multi-select',
    label: descriptor.label,
    placeholder: descriptor.placeholder,
    defaultValue: [],
    options: descriptor.source ?? descriptor.choices,
  };
}

export function ReportBuilderTab() {
  const meEmployeeId = useAuthStore((s) => s.user?.employee?.id);
  const canShare = usePermission('time.reports.share');
  // "Describe the report you want" is the only part of the Time Tracker that
  // calls a model. Hidden with the assistant off — the backend refuses the same
  // request in a sentence, so the two agree if a stale tab tries anyway.
  const isAssistantEnabled = useExtensionsStore((s) => s.isExtensionEnabled('ai-assistant'));

  const [config, setConfig] = useState<TimeReportConfig>(() =>
    cloneConfig(DEFAULT_TIME_REPORT_CONFIG),
  );
  /** Filters opened by hand that have not selected anything yet. */
  const [opened, setOpened] = useState<ReportFilterKey[]>([]);
  const [description, setDescription] = useState('');
  const [unresolved, setUnresolved] = useState<Array<{ field: string; text: string }>>([]);
  const [result, setResult] = useState<TimeReportRunResult | null>(null);
  /** The configuration the result was computed from, so a stale table can say so. */
  const [ranConfig, setRanConfig] = useState<string | null>(null);
  const [loaded, setLoaded] = useState<SavedTimeReport | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  /**
   * The Save-as / rename dialog.
   *
   * Rename carries the report it is renaming rather than reading whichever one
   * happens to be open: renaming from the rail would otherwise read a `loaded`
   * that the click has only just asked React to change, and rename the previous
   * report under the new name.
   */
  const [saveDialog, setSaveDialog] = useState<
    null | { mode: 'create' } | { mode: 'rename'; report: SavedTimeReport }
  >(null);
  const [saveName, setSaveName] = useState('');
  const [saveShared, setSaveShared] = useState(false);
  const [deleting, setDeleting] = useState<SavedTimeReport | null>(null);

  const run = useRunTimeReport();
  const draft = useDraftReportConfig();
  const savedReports = useSavedReports();
  const saveReport = useSaveReport();
  const updateReport = useUpdateSavedReport();
  const duplicateReport = useDuplicateSavedReport();
  const deleteReport = useDeleteSavedReport();

  const grouping = useMemo(() => groupingOf(config.groupBy), [config.groupBy]);
  const visible = useMemo(() => visibleFilterKeys(config, opened), [config, opened]);
  const available = useMemo(() => availableFilterKeys(config, opened), [config, opened]);
  const { mine, shared } = useMemo(
    () => splitSavedReports(savedReports.data ?? [], meEmployeeId),
    [savedReports.data, meEmployeeId],
  );

  const filterLabels = useFilterOptionLabels();

  // Compared by fingerprint, not by JSON: a config from the server and one
  // rebuilt by the filter row spell their keys in different orders, so
  // stringifying reported a change after an interaction that made none.
  const isStale = ranConfig !== null && ranConfig !== configFingerprint(config);

  // ── editing the configuration ─────────────────────────────────────────
  const patch = useCallback((next: Partial<TimeReportConfig>) => {
    setConfig((current) => ({ ...current, ...next }));
  }, []);

  const setFilters = useCallback((filters: TimeReportConfig['filters']) => {
    setConfig((current) => ({ ...current, filters }));
  }, []);

  const openFilter = (key: ReportFilterKey) => setOpened((keys) => [...keys, key]);

  const removeFilter = (key: ReportFilterKey) => {
    setOpened((keys) => keys.filter((existing) => existing !== key));
    setFilters(clearFilter(config.filters, key));
  };

  // ── running ───────────────────────────────────────────────────────────
  const handleRun = () => {
    const snapshot = configFingerprint(config);
    run.mutate(config, {
      onSuccess: (data) => {
        setResult(data);
        setRanConfig(snapshot);
      },
    });
  };

  // ── describing ────────────────────────────────────────────────────────
  const handleDraft = () => {
    if (description.trim().length < 3) return;
    draft.mutate(description.trim(), {
      onSuccess: (data) => {
        // The draft fills the FORM. Nothing runs, and the result already on
        // screen is left alone rather than being re-labelled with a
        // configuration that did not produce it.
        setConfig(cloneConfig(data.config));
        setUnresolved(data.unresolved);
        // Everything the drafted configuration uses is visible by construction
        // (`visibleFilterKeys` expands active filters); clearing the by-hand
        // list keeps a filter the previous configuration opened from lingering
        // empty beside the new one.
        setOpened([]);
        setLoaded(null);
      },
    });
  };

  // ── saved reports ─────────────────────────────────────────────────────
  const handleLoad = (report: SavedTimeReport) => {
    const parsed = parseSavedConfig(report.config);
    if (!parsed.ok) {
      // Never fall back to the default configuration: running a different
      // report under this one's name is worse than saying it is broken.
      setLoadError(`"${report.name}" cannot be opened — ${parsed.message}`);
      return;
    }
    setLoadError(null);
    setConfig(parsed.config);
    setLoaded(report);
    setOpened([]);
    setUnresolved([]);
    setResult(null);
    setRanConfig(null);
  };

  const openSaveAs = () => {
    setSaveName(loaded ? `${loaded.name} (copy)` : '');
    setSaveShared(false);
    setSaveDialog({ mode: 'create' });
  };

  const openRename = (report: SavedTimeReport) => {
    setSaveName(report.name);
    setSaveShared(report.scope === 'shared');
    setSaveDialog({ mode: 'rename', report });
  };

  const handleSaveDialogConfirm = () => {
    const name = saveName.trim();
    if (!name) return;
    /**
     * Undefined when the switch is disabled, so the scope is not written at all.
     *
     * `saveShared && canShare ? 'shared' : 'personal'` sent 'personal' whenever
     * the actor lacked the permission — and the API allows that write, since
     * only a change TO shared is a publication. So somebody who shared a report
     * and later lost the permission un-shared it for everyone by renaming it,
     * under a dialog promising that "only the name and who can see it change".
     */
    const scope = canShare ? (saveShared ? 'shared' : 'personal') : undefined;

    if (saveDialog?.mode === 'rename') {
      const target = saveDialog.report;
      updateReport.mutate(
        { id: target.id, name, scope },
        {
          onSuccess: (report) => {
            // Only re-point the open report when it is the one that changed.
            setLoaded((current) => (current?.id === report.id ? report : current));
            setSaveDialog(null);
          },
        },
      );
      return;
    }

    saveReport.mutate(
      // Creating needs a scope, and without the permission there is only one it
      // could be.
      { name, scope: scope ?? 'personal', config },
      {
        onSuccess: (report) => {
          setLoaded(report);
          setSaveDialog(null);
        },
      },
    );
  };

  /** Save over the open report. Only offered when the API would allow it. */
  const canOverwrite = loaded ? canManageReport(loaded, meEmployeeId, canShare) : false;

  const handleOverwrite = () => {
    if (!loaded) return;
    updateReport.mutate({ id: loaded.id, config }, { onSuccess: (report) => setLoaded(report) });
  };

  // ── export ────────────────────────────────────────────────────────────

  /**
   * Everything an export needs, taken from the RESULT rather than from the
   * form: the form can have been edited since Run, and a document headed with
   * a configuration that did not produce its numbers is worse than no
   * document. Both formats read this one object, so the spreadsheet and the
   * PDF cannot describe different reports.
   */
  const exportInput = () =>
    result && {
      config: result.config,
      window: result.window,
      totalSeconds: result.totalSeconds,
      rows: result.rows,
      generatedOn: new Date(),
      // The file leaves the building without the screen around it, so the reason
      // its window stops short has to travel with it. [HW-850]
      openPeriodExcluded: result.openPeriodExcluded,
      // So a document can print "Employment types: Contractor (in house)"
      // rather than leaving a narrowed report looking like a whole-company
      // one. Ids it cannot name are counted, never dropped.
      filterLabels,
    };

  // Both formats go through the shared exporter, so the Reports screen's
  // summarized shapes and this one produce the same files. [HW-877]
  const handleExportSheet = (format: 'xlsx' | 'csv') => {
    const input = exportInput();
    if (input) void exportBuiltReport(format, input);
  };

  const handleExportPdf = () => {
    const input = exportInput();
    if (input) void exportBuiltReport('pdf', input);
  };

  const filterProviderConfig = useMemo<FilterConfig>(
    () => ({
      id: 'time-report-builder',
      fields: REPORT_FILTERS.map((descriptor) =>
        descriptor.kind === 'list'
          ? (fieldConfigOf(descriptor.key) as FilterFieldConfig)
          : ({
              key: descriptor.key,
              type: 'select',
              label: descriptor.label,
              placeholder: descriptor.placeholder,
              defaultValue: 'all',
              options: descriptor.choices,
            } as FilterFieldConfig),
      ),
      defaultValues: toFilterState({ billable: 'all' }),
    }),
    [],
  );

  return (
    <div className="grid gap-6 lg:grid-cols-[240px_1fr]">
      <SavedRail
        isLoading={savedReports.isLoading}
        error={savedReports.error}
        mine={mine}
        shared={shared}
        loadedId={loaded?.id ?? null}
        meEmployeeId={meEmployeeId}
        canShare={canShare}
        onLoad={handleLoad}
        onRename={openRename}
        onDuplicate={(report) => duplicateReport.mutate(report.id)}
        onToggleShare={(report) =>
          updateReport.mutate({
            id: report.id,
            scope: report.scope === 'shared' ? 'personal' : 'shared',
          })
        }
        onDelete={setDeleting}
      />

      <div className="space-y-6">
        {loadError && <Notice tone="warning">{loadError}</Notice>}

        {isAssistantEnabled && (
          <DescribeBox
            description={description}
            onDescriptionChange={setDescription}
            onDraft={handleDraft}
            isPending={draft.isPending}
            error={draft.error}
            unresolved={unresolved}
            onDismissUnresolved={() => setUnresolved([])}
          />
        )}

        <Card>
          <CardContent className="space-y-6 pt-6">
            <PeriodRow config={config} onPatch={patch} />

            <FilterProvider
              config={filterProviderConfig}
              value={toFilterState(config.filters)}
              onChange={(state) => setFilters(fromFilterState(state, config.filters))}
            >
              <FilterRow
                visible={visible}
                available={available}
                onOpen={openFilter}
                onRemove={removeFilter}
                completedPeriodsOnly={config.filters.completedPeriodsOnly ?? false}
                onToggleCompletedPeriods={(next) =>
                  patch({ filters: setCompletedPeriodsOnly(config.filters, next) })
                }
              />
            </FilterProvider>

            <GroupingRow
              grouping={grouping}
              onChange={(next) => patch({ groupBy: groupByOf(next) })}
            />

            <div className="flex flex-wrap items-end gap-6">
              <div className="space-y-2">
                <Label className="text-xs font-medium text-muted-foreground">Columns</Label>
                <div className="flex flex-wrap gap-2">
                  {REPORT_METRICS.map(({ metric, label }) => (
                    <Toggle
                      key={metric}
                      pressed={config.metrics.includes(metric)}
                      onClick={() => patch({ metrics: toggleMetric(config.metrics, metric) })}
                    >
                      {label}
                    </Toggle>
                  ))}
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-xs font-medium text-muted-foreground">View</Label>
                <Select
                  value={config.view}
                  onValueChange={(value) => patch({ view: value as TimeReportConfig['view'] })}
                >
                  <SelectTrigger className="h-9 w-[170px] text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="table">Table</SelectItem>
                    <SelectItem value="table+chart">Table and chart</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="ml-auto flex flex-wrap items-center gap-2">
                {loaded && canOverwrite && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleOverwrite}
                    disabled={updateReport.isPending}
                    className="gap-1.5"
                  >
                    <Save className="h-3.5 w-3.5" />
                    Save
                  </Button>
                )}
                <Button variant="outline" size="sm" onClick={openSaveAs} className="gap-1.5">
                  <Save className="h-3.5 w-3.5" />
                  Save as…
                </Button>
                <Button size="sm" onClick={handleRun} disabled={run.isPending} className="gap-1.5">
                  {run.isPending ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <Play className="h-3.5 w-3.5" />
                  )}
                  Run
                </Button>
              </div>
            </div>

            {usesPeopleAttributes(config) && (
              <p className="text-xs text-muted-foreground">{PEOPLE_ATTRIBUTE_NOTICE}</p>
            )}

            {(saveReport.error || updateReport.error || deleteReport.error) && (
              <Notice tone="warning">
                {extractErrorMessage(
                  saveReport.error ?? updateReport.error ?? deleteReport.error,
                  'That saved report could not be changed.',
                )}
              </Notice>
            )}
          </CardContent>
        </Card>

        <ResultSection
          result={result}
          isPending={run.isPending}
          error={run.error}
          isStale={isStale}
          filterLabels={filterLabels}
          onExportSheet={handleExportSheet}
          onExportPdf={handleExportPdf}
        />
      </div>

      <Dialog open={saveDialog !== null} onOpenChange={(open) => !open && setSaveDialog(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {saveDialog?.mode === 'rename' ? 'Rename this report' : 'Save this report'}
            </DialogTitle>
            <DialogDescription>
              {saveDialog?.mode === 'rename'
                ? 'The configuration is untouched — only the name and who can see it change.'
                : 'The configuration is stored as it stands. Running it later re-computes the numbers.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="saved-report-name">Name</Label>
              <Input
                id="saved-report-name"
                value={saveName}
                onChange={(event) => setSaveName(event.target.value)}
                placeholder="Billable hours by project"
                maxLength={120}
              />
            </div>
            <label className="flex items-center gap-2">
              <Switch checked={saveShared} onCheckedChange={setSaveShared} disabled={!canShare} />
              <span className="text-sm">
                Share with everyone who can generate reports
                {!canShare && (
                  <span className="block text-xs text-muted-foreground">
                    Sharing needs the “share time reports” permission.
                  </span>
                )}
              </span>
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSaveDialog(null)}>
              Cancel
            </Button>
            <Button
              onClick={handleSaveDialogConfirm}
              disabled={!saveName.trim() || saveReport.isPending || updateReport.isPending}
            >
              {saveDialog?.mode === 'rename' ? 'Rename' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={deleting !== null}
        onOpenChange={(open) => !open && setDeleting(null)}
        destructive
        title={`Delete “${deleting?.name ?? ''}”?`}
        description={
          deleting
            ? deleting.scope === 'shared'
              ? `This report belongs to ${ownerName(deleting)} and is shared — deleting it removes it for everyone.`
              : `This report belongs to ${ownerName(deleting)}.`
            : undefined
        }
        confirmLabel="Delete"
        isPending={deleteReport.isPending}
        onConfirm={() => {
          if (!deleting) return;
          deleteReport.mutate(deleting.id, {
            onSuccess: () => {
              if (loaded?.id === deleting.id) setLoaded(null);
              setDeleting(null);
            },
          });
        }}
      />
    </div>
  );
}

// ─── Describe ─────────────────────────────────────────────────────────────

function DescribeBox({
  description,
  onDescriptionChange,
  onDraft,
  isPending,
  error,
  unresolved,
  onDismissUnresolved,
}: {
  description: string;
  onDescriptionChange: (value: string) => void;
  onDraft: () => void;
  isPending: boolean;
  error: unknown;
  unresolved: Array<{ field: string; text: string }>;
  onDismissUnresolved: () => void;
}) {
  return (
    <Card>
      <CardContent className="space-y-3 pt-6">
        <div className="flex items-start gap-3">
          <Textarea
            value={description}
            onChange={(event) => onDescriptionChange(event.target.value)}
            placeholder="Describe the report you want — “billable hours per project for the design team last month”"
            rows={2}
            maxLength={1000}
            className="flex-1 resize-none"
          />
          <Button
            onClick={onDraft}
            disabled={isPending || description.trim().length < 3}
            className="gap-1.5"
          >
            {isPending ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Sparkles className="h-3.5 w-3.5" />
            )}
            Fill the form
          </Button>
        </div>
        <p className="text-xs text-muted-foreground">
          The description fills the form below. Nothing runs until you press Run, and every number
          comes from the report itself.
        </p>

        {error != null && (
          <Notice tone="warning">
            {extractErrorMessage(error, 'That description could not be turned into a report.')}
          </Notice>
        )}

        {unresolved.length > 0 && (
          // Where the eye already is — directly under the sentence that caused
          // it, not muted below the form. An unresolved name means that filter
          // is EMPTY, so the report would answer a wider question than the one
          // asked unless somebody picks the value by hand.
          <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 dark:border-amber-500/40 dark:bg-amber-500/10">
            <div className="flex items-start gap-2">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400" />
              <div className="flex-1 space-y-1">
                <p className="text-sm font-medium text-amber-900 dark:text-amber-200">
                  Some of that could not be placed — pick it by hand below, or the report will not
                  narrow on it.
                </p>
                <ul className="space-y-0.5">
                  {unresolved.map((item, index) => (
                    <li key={`${item.field}-${index}`} className="text-sm text-amber-900 dark:text-amber-200">
                      <span className="font-medium">“{item.text}”</span>{' '}
                      <span className="text-amber-700 dark:text-amber-300/80">
                        for {unresolvedFieldLabel(item.field)}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
              <button
                type="button"
                onClick={onDismissUnresolved}
                className="text-amber-700 hover:text-amber-900 dark:text-amber-300 cursor-pointer"
                aria-label="Dismiss"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ─── Period ───────────────────────────────────────────────────────────────

function PeriodRow({
  config,
  onPatch,
}: {
  config: TimeReportConfig;
  onPatch: (next: Partial<TimeReportConfig>) => void;
}) {
  const { period } = config;

  return (
    <div className="flex flex-wrap items-end gap-4">
      <div className="space-y-1.5">
        <Label className="text-xs font-medium text-muted-foreground">Period</Label>
        <Select
          value={period.preset}
          onValueChange={(value) =>
            onPatch({
              period: {
                ...period,
                preset: value as TimeReportConfig['period']['preset'],
                // A custom range keeps whatever dates are already picked, and
                // starts on today when there are none — the schema refuses a
                // custom period with a missing end.
                ...(value === 'custom'
                  ? {
                      from: period.from ?? toDayString(new Date()),
                      to: period.to ?? toDayString(new Date()),
                    }
                  : {}),
              },
            })
          }
        >
          <SelectTrigger className="h-9 w-[170px] text-sm">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {PERIOD_PRESETS.map((preset) => (
              <SelectItem key={preset.value} value={preset.value}>
                {preset.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {period.preset === 'custom' && (
        <>
          {/* Wide enough for the picker's widest date at either font size the input takes —
              see formatDate in ui/date-picker.tsx. */}
          <div className="space-y-1.5">
            <Label className="text-xs font-medium text-muted-foreground">From</Label>
            <DatePicker
              value={fromDayString(period.from)}
              onChange={(date) =>
                onPatch({ period: { ...period, from: date ? toDayString(date) : undefined } })
              }
              placeholder="Start date"
              maxDate={fromDayString(period.to) ?? undefined}
              className="w-[220px]"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-medium text-muted-foreground">To</Label>
            <DatePicker
              value={fromDayString(period.to)}
              onChange={(date) =>
                onPatch({ period: { ...period, to: date ? toDayString(date) : undefined } })
              }
              placeholder="End date"
              minDate={fromDayString(period.from) ?? undefined}
              className="w-[220px]"
            />
          </div>
        </>
      )}
    </div>
  );
}

// ─── Filters ──────────────────────────────────────────────────────────────

/**
 * The filter row: project and person always, everything else as a chip.
 *
 * State comes from `FilterProvider` in controlled mode, so the configuration
 * stays the single source of truth and the provider is a view over it — that is
 * what makes `resetFilters` here mean the same thing as clearing the config's
 * filters.
 */
function FilterRow({
  visible,
  available,
  onOpen,
  onRemove,
  completedPeriodsOnly,
  onToggleCompletedPeriods,
}: {
  visible: ReportFilterKey[];
  available: ReportFilterKey[];
  onOpen: (key: ReportFilterKey) => void;
  onRemove: (key: ReportFilterKey) => void;
  completedPeriodsOnly: boolean;
  onToggleCompletedPeriods: (next: boolean) => void;
}) {
  const { filters, setFilter, resetFilters, activeFilterCount } =
    useFilterContext<ReportFilterState>();

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <Label className="text-xs font-medium text-muted-foreground">Filters</Label>

        {activeFilterCount > 0 && (
          <>
            <Badge variant="secondary">{activeFilterCount} active</Badge>
            <button
              type="button"
              onClick={resetFilters}
              className="cursor-pointer text-xs text-muted-foreground underline-offset-4 hover:text-foreground hover:underline"
            >
              Clear all
            </button>
          </>
        )}
        {/* Outside the chip system on purpose: one switch over the whole report,
            not a criterion with values. So it carries no chip, `Clear all` leaves
            it alone, and it shows its own state at all times instead. Last in the
            row, so the badge and `Clear all` keep the places they had. */}
        <div className="ml-auto">
          <Toggle
            pressed={completedPeriodsOnly}
            onClick={() => onToggleCompletedPeriods(!completedPeriodsOnly)}
          >
            Completed periods only
          </Toggle>
        </div>
      </div>

      <div className="flex flex-wrap items-start gap-3">
        {visible.map((key) => {
          const descriptor = filterDescriptor(key);
          const value = filters[key];
          return (
            <div
              key={key}
              className="min-w-[230px] space-y-1.5 rounded-lg border border-border bg-background p-2.5"
            >
              <div className="flex items-center justify-between gap-2">
                <span className="text-xs font-medium text-foreground">{descriptor.label}</span>
                {!descriptor.pinned && (
                  <button
                    type="button"
                    onClick={() => onRemove(key)}
                    className="cursor-pointer text-muted-foreground hover:text-foreground"
                    aria-label={`Remove the ${descriptor.label} filter`}
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
              {descriptor.kind === 'list' ? (
                <MultiSelectFilter
                  config={fieldConfigOf(key)}
                  value={Array.isArray(value) ? value : []}
                  onChange={(next) => setFilter(key, next)}
                  hideLabel
                />
              ) : (
                <Select
                  value={typeof value === 'string' ? value : 'all'}
                  onValueChange={(next) => setFilter(key, next)}
                >
                  <SelectTrigger className="h-9 w-full text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {(descriptor.choices ?? []).map((choice) => (
                      <SelectItem key={choice.value} value={choice.value}>
                        {choice.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
          );
        })}

        {available.length > 0 && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="mt-6 gap-1.5">
                <Plus className="h-3.5 w-3.5" />
                Add filter
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start">
              {available.map((key) => (
                <DropdownMenuItem key={key} onSelect={() => onOpen(key)}>
                  {filterDescriptor(key).label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
    </div>
  );
}

// ─── Grouping ─────────────────────────────────────────────────────────────

function GroupingRow({
  grouping,
  onChange,
}: {
  grouping: ReturnType<typeof groupingOf>;
  onChange: (next: ReturnType<typeof groupingOf>) => void;
}) {
  const axisAllowed = canSetTimeAxis(grouping);
  const chosen = groupByOf(grouping);

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <Label className="text-xs font-medium text-muted-foreground">Group by</Label>
        <span className="text-xs text-muted-foreground">
          {chosen.map(levelLabel).join(' → ')}
        </span>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        {GROUP_LEVELS.map(({ level, label }) => {
          const pressed = grouping.levels.includes(level);
          const atCapacity =
            !pressed &&
            grouping.levels.length + (grouping.axis === 'none' ? 0 : 1) >= MAX_GROUP_LEVELS;
          return (
            <Toggle
              key={level}
              pressed={pressed}
              disabled={atCapacity}
              onClick={() => onChange(toggleLevel(grouping, level))}
            >
              {label}
            </Toggle>
          );
        })}

        <div className="ml-2 flex items-center gap-2">
          {/* Day and week are one axis offered once: a day and the week it falls
              in are the same dimension asked for twice, which the schema
              refuses outright. */}
          <span className="text-xs text-muted-foreground">Over time</span>
          <Select
            value={grouping.axis}
            onValueChange={(value) =>
              onChange(setTimeAxis(grouping, value as 'none' | 'day' | 'week'))
            }
          >
            <SelectTrigger className="h-9 w-[130px] text-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">No time axis</SelectItem>
              <SelectItem value="day" disabled={!axisAllowed}>
                By day
              </SelectItem>
              <SelectItem value="week" disabled={!axisAllowed}>
                By week
              </SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      <p className="text-xs text-muted-foreground">
        At most two levels, and at most one of them over time.
        {!axisAllowed && ' Remove a grouping to add a day or week axis.'}
      </p>
    </div>
  );
}

// ─── Result ───────────────────────────────────────────────────────────────

function ResultSection({
  result,
  isPending,
  error,
  isStale,
  filterLabels,
  onExportSheet,
  onExportPdf,
}: {
  result: TimeReportRunResult | null;
  isPending: boolean;
  error: unknown;
  isStale: boolean;
  filterLabels: FilterOptionLabels;
  onExportSheet: (format: 'xlsx' | 'csv') => void;
  onExportPdf: () => void;
}) {
  // The empty state offers describing the report; only offer it while the box
  // that does it is on screen.
  const isAssistantEnabled = useExtensionsStore((s) => s.isExtensionEnabled('ai-assistant'));

  if (isPending) {
    return (
      <Card>
        <CardContent className="space-y-3 pt-6">
          <p className="text-sm text-muted-foreground">Running the report…</p>
          <Skeleton className="h-40 w-full" />
        </CardContent>
      </Card>
    );
  }

  if (error != null) {
    return (
      <Notice tone="warning">
        {/* The window guard refuses a period it cannot serve and says which in
            its message — repeating its sentence beats inventing a vaguer one. */}
        {extractErrorMessage(error, 'That report could not be run.')}
      </Notice>
    );
  }

  if (!result) {
    return (
      <Card>
        <CardContent className="py-12 text-center text-sm text-muted-foreground">
          Nothing has been run yet. Set the report up above
          {isAssistantEnabled ? ' — or describe it — ' : ' '}
          and press Run.
        </CardContent>
      </Card>
    );
  }

  const groupBy = result.config.groupBy;
  const metrics = result.config.metrics;
  const grownToWeeks = groupBy.includes('week');
  const agrees = totalsAgree(result.rows, result.totalSeconds);
  const leafCount = result.rows.filter((row) => !row.subtotal).length;
  // From the configuration the SERVER ran, so what is listed here is what
  // shaped the total beside it — and the same list the export prints.
  const applied = describeFilters(result.config, filterLabels);

  return (
    <Card>
      <CardContent className="space-y-4 pt-6">
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
          <span className="flex items-center gap-2 text-sm text-muted-foreground">
            <FileText className="h-4 w-4" />
            {/* The window the SERVER resolved, always. */}
            {formatWindow(result.window)}
          </span>
          {result.openPeriodExcluded && (
            <span className="text-sm text-muted-foreground">
              {/* Rendered from the server's answer, never worked out here: a zero
                  that explains itself is the whole point of the filter. */}
              {result.openPeriodExcluded.monthLabel} onward is still open and is not counted.
            </span>
          )}
          <span className="text-sm font-medium text-foreground">
            Total: {formatDuration(result.totalSeconds)}
          </span>
          <span className="text-sm text-muted-foreground">
            {/* The report's rows, not its group headers — a two-level report
                carries one total line per outer group, and counting those
                would say "26 rows" of a twenty-row report. [HW-877] */}
            {leafCount} {leafCount === 1 ? 'row' : 'rows'}
          </span>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                size="sm"
                variant="outline"
                disabled={result.rows.length === 0}
                className="ml-auto gap-1.5"
              >
                <Download className="h-3.5 w-3.5" />
                Export
              </Button>
            </DropdownMenuTrigger>
            {/* Excel first, because it is what this export was missing: the
                Builder could only ever print, so a summarized report was
                readable but not workable. The Reports tab offers the same
                three, in the same order. [HW-877] */}
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => onExportSheet('xlsx')} className="gap-2">
                <Download className="h-4 w-4" /> Excel (.xlsx)
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => onExportSheet('csv')} className="gap-2">
                <Download className="h-4 w-4" /> CSV (.csv)
              </DropdownMenuItem>
              <DropdownMenuItem onClick={onExportPdf} className="gap-2">
                <Download className="h-4 w-4" /> PDF
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {applied.length > 0 && (
          <p className="text-xs text-muted-foreground">
            <span className="font-medium text-foreground">Filtered by</span>{' '}
            {applied.map((filter) => `${filter.label}: ${filter.text}`).join(' · ')}
          </p>
        )}

        {grownToWeeks && (
          <p className="text-xs text-muted-foreground">
            {result.openPeriodExcluded ? WEEK_WINDOW_CUT_NOTICE : WEEK_WINDOW_NOTICE}
          </p>
        )}

        {usesPeopleAttributes(result.config) && (
          <p className="text-xs text-muted-foreground">{PEOPLE_ATTRIBUTE_NOTICE}</p>
        )}

        {isStale && (
          <Notice tone="muted">
            The configuration has changed since this ran. These numbers describe the settings that
            produced them — press Run to bring them up to date.
          </Notice>
        )}

        {!agrees && (
          // Every query path in the runner derives from one predicate, so the
          // rows and the headline cannot disagree. If they ever do, the reader
          // hears it here rather than trusting a total the table contradicts.
          <Notice tone="warning">
            These rows add to {formatDuration(rowsSumSeconds(result.rows))}, and the report's total
            is{' '}
            {formatDuration(result.totalSeconds)}. One of them is wrong — do not use this report
            until it is fixed.
          </Notice>
        )}

        {result.rows.length === 0 ? (
          <p className="py-8 text-center text-sm text-muted-foreground">
            No hours matched this configuration in that window. Widen the period, or clear a filter.
          </p>
        ) : (
          <>
            {result.config.view === 'table+chart' && (
              <ResultChart rows={result.rows} outerLevel={groupBy[0]} />
            )}
            <ResultTable
              rows={result.rows}
              groupBy={groupBy}
              metrics={metrics}
              totalSeconds={result.totalSeconds}
            />
          </>
        )}
      </CardContent>
    </Card>
  );
}

function ResultChart({
  rows,
  outerLevel,
}: {
  rows: TimeReportRunResult['rows'];
  outerLevel: TimeReportGroupByLevel;
}) {
  const series = useMemo(() => chartSeries(rows, outerLevel), [rows, outerLevel]);
  const data = useMemo(
    () =>
      series.bars.map((entry) => ({ ...entry, hours: Number(formatHours(entry.totalSeconds)) })),
    [series],
  );

  // A trend can run to 53 weeks; a comparison never exceeds a dozen. Thinner
  // rows keep a long time axis on one screen-worth of scroll instead of ten.
  const rowHeight = data.length > 24 ? 16 : 30;

  return (
    <div className="space-y-2">
      <ChartContainer
        config={HOURS_CHART}
        className="aspect-auto w-full"
        style={{ height: data.length * rowHeight + 24 }}
      >
        <BarChart data={data} layout="vertical" margin={{ left: 8, right: 40, top: 4, bottom: 4 }}>
          <CartesianGrid horizontal={false} />
          <XAxis type="number" dataKey="hours" hide />
          <YAxis
            type="category"
            dataKey="label"
            width={150}
            tickLine={false}
            axisLine={false}
            tick={{ fontSize: 12 }}
          />
          <ChartTooltip cursor={false} content={<ChartTooltipContent hideLabel />} />
          <Bar dataKey="hours" fill="var(--color-hours)" radius={4} />
        </BarChart>
      </ChartContainer>
      {series.hiddenCount > 0 && (
        // Said out loud, because a chart of twelve bars under a total
        // describing 824 groups reads as the whole report otherwise.
        <p className="text-xs text-muted-foreground">
          The chart shows {data.length} of {data.length + series.hiddenCount} groups — the table
          below lists them all.
        </p>
      )}
    </div>
  );
}

// ─── Saved reports rail ───────────────────────────────────────────────────

function SavedRail({
  isLoading,
  error,
  mine,
  shared,
  loadedId,
  meEmployeeId,
  canShare,
  onLoad,
  onRename,
  onDuplicate,
  onToggleShare,
  onDelete,
}: {
  isLoading: boolean;
  error: unknown;
  mine: SavedTimeReport[];
  shared: SavedTimeReport[];
  loadedId: string | null;
  meEmployeeId: string | undefined;
  canShare: boolean;
  onLoad: (report: SavedTimeReport) => void;
  onRename: (report: SavedTimeReport) => void;
  onDuplicate: (report: SavedTimeReport) => void;
  onToggleShare: (report: SavedTimeReport) => void;
  onDelete: (report: SavedTimeReport) => void;
}) {
  return (
    <aside className="space-y-4">
      <h3 className="text-sm font-semibold text-foreground">Saved reports</h3>

      {isLoading ? (
        <div className="space-y-2">
          <Skeleton className="h-8 w-full" />
          <Skeleton className="h-8 w-full" />
        </div>
      ) : error != null ? (
        <p className="text-xs text-muted-foreground">
          {extractErrorMessage(error, 'Saved reports could not be loaded.')}
        </p>
      ) : (
        <>
          <RailSection title="Mine" empty="Nothing saved yet — build a report and press Save as.">
            {mine.map((report) => (
              <RailRow
                key={report.id}
                report={report}
                active={report.id === loadedId}
                canManage={canManageReport(report, meEmployeeId, canShare)}
                canShare={canShare}
                onLoad={onLoad}
                onRename={onRename}
                onDuplicate={onDuplicate}
                onToggleShare={onToggleShare}
                onDelete={onDelete}
              />
            ))}
          </RailSection>

          <RailSection title="Shared" empty="Nobody has shared a report yet.">
            {shared.map((report) => (
              <RailRow
                key={report.id}
                report={report}
                active={report.id === loadedId}
                canManage={canManageReport(report, meEmployeeId, canShare)}
                canShare={canShare}
                onLoad={onLoad}
                onRename={onRename}
                onDuplicate={onDuplicate}
                onToggleShare={onToggleShare}
                onDelete={onDelete}
              />
            ))}
          </RailSection>
        </>
      )}
    </aside>
  );
}

function RailSection({
  title,
  empty,
  children,
}: {
  title: string;
  empty: string;
  children: ReactNode[];
}) {
  return (
    <div className="space-y-1.5">
      <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{title}</p>
      {children.length === 0 ? (
        <p className="text-xs text-muted-foreground">{empty}</p>
      ) : (
        <ul className="space-y-1">{children}</ul>
      )}
    </div>
  );
}

function RailRow({
  report,
  active,
  canManage,
  canShare,
  onLoad,
  onRename,
  onDuplicate,
  onToggleShare,
  onDelete,
}: {
  report: SavedTimeReport;
  active: boolean;
  canManage: boolean;
  canShare: boolean;
  onLoad: (report: SavedTimeReport) => void;
  onRename: (report: SavedTimeReport) => void;
  onDuplicate: (report: SavedTimeReport) => void;
  onToggleShare: (report: SavedTimeReport) => void;
  onDelete: (report: SavedTimeReport) => void;
}) {
  return (
    <li
      className={cn(
        'group flex items-center gap-1 rounded-md px-2 py-1.5',
        active ? 'bg-accent' : 'hover:bg-accent/60',
      )}
    >
      <button
        type="button"
        onClick={() => onLoad(report)}
        className="min-w-0 flex-1 cursor-pointer text-left"
      >
        <span className="block truncate text-sm text-foreground">{report.name}</span>
        {report.scope === 'shared' && (
          <span className="block truncate text-xs text-muted-foreground">
            {ownerName(report)}
          </span>
        )}
      </button>

      {/* Gated by the same rule the API enforces, so nobody discovers a 403
          after pressing Save — which is exactly what the Export template list
          does today. Duplicate is offered to everyone: copying a report you can
          see needs no permission. */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            className="cursor-pointer rounded p-1 text-muted-foreground opacity-0 hover:text-foreground group-hover:opacity-100 focus:opacity-100"
            aria-label={`Actions for ${report.name}`}
          >
            <MoreHorizontal className="h-4 w-4" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onSelect={() => onDuplicate(report)}>
            <Copy className="mr-2 h-4 w-4" />
            Duplicate
          </DropdownMenuItem>
          {canManage && (
            <>
              <DropdownMenuItem onSelect={() => onRename(report)}>
                <Pencil className="mr-2 h-4 w-4" />
                Rename
              </DropdownMenuItem>
              {/* Publishing needs `time.reports.share`; withdrawing does not —
                  `assertMayPublish` only guards a change TO shared. Offering
                  "Share" to an owner without the permission was the exact thing
                  the comment above claims this menu prevents: the API answers
                  403 after the click. */}
              {(report.scope === 'shared' || canShare) && (
                <DropdownMenuItem onSelect={() => onToggleShare(report)}>
                  <Share2 className="mr-2 h-4 w-4" />
                  {report.scope === 'shared' ? 'Make personal' : 'Share'}
                </DropdownMenuItem>
              )}
              <DropdownMenuItem onSelect={() => onDelete(report)} className="text-destructive">
                <Trash2 className="mr-2 h-4 w-4" />
                Delete
              </DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </li>
  );
}

// ─── Small pieces ─────────────────────────────────────────────────────────

function Toggle({
  pressed,
  disabled,
  onClick,
  children,
}: {
  pressed: boolean;
  disabled?: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      aria-pressed={pressed}
      disabled={disabled}
      onClick={onClick}
      className={cn(
        'rounded-md border px-2.5 py-1.5 text-xs font-medium transition-colors',
        disabled ? 'cursor-not-allowed opacity-40' : 'cursor-pointer',
        pressed
          ? 'border-hygge bg-hygge/10 text-foreground'
          : 'border-border bg-background text-muted-foreground hover:text-foreground',
      )}
    >
      {children}
    </button>
  );
}

function Notice({
  tone,
  children,
}: {
  tone: 'warning' | 'muted';
  children: React.ReactNode;
}) {
  return (
    <div
      className={cn(
        'rounded-lg border p-3 text-sm',
        tone === 'warning'
          ? 'border-amber-300 bg-amber-50 text-amber-900 dark:border-amber-500/40 dark:bg-amber-500/10 dark:text-amber-200'
          : 'border-border bg-muted/40 text-muted-foreground',
      )}
    >
      {children}
    </div>
  );
}
